import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle, CheckCircle, XCircle, Clock, Filter,
  ChevronDown, ChevronUp, ArrowRight, RefreshCw, Loader2,
  Eye, EyeOff, BarChart3, TrendingUp, TrendingDown, FlaskConical
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Line, ComposedChart } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';

const TYPE_COLORS = {
  lei_averaged: { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-200' },
  lei_raw: { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200' },
  pe_capability: { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-200' },
  pe_retention: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
  roe_l2: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-200' },
  roe_motivation: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  roe_l3: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-200' },
  kpi_actuals: { bg: 'bg-rose-100', text: 'text-rose-800', border: 'border-rose-200' },
};

const SEVERITY_CONFIG = {
  critical: { icon: AlertTriangle, color: 'text-rose-600', bg: 'bg-rose-50', border: 'border-rose-200', label: 'Critical' },
  warning: { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', label: 'Warning' },
  info: { icon: BarChart3, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Info' },
};

const STATUS_CONFIG = {
  open: { icon: Clock, color: 'text-orange-600', bg: 'bg-orange-50', label: 'Open' },
  acknowledged: { icon: Eye, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Reviewed' },
  dismissed: { icon: EyeOff, color: 'text-slate-500', bg: 'bg-slate-50', label: 'Dismissed' },
};

const ROUTE_MAP = {
  kpi_actuals: '/score-trends?tab=kpi',
  lei_averaged: '/score-trends?tab=lei',
  lei_raw: '/score-trends?tab=lei',
  pe_capability: '/people-engine',
  pe_retention: '/people-engine',
  roe_l2: '/roe-health',
  roe_motivation: '/roe-health',
  roe_l3: '/roe-health',
};

function AnomalyRow({ event, onAcknowledge, onDismiss, isActioning }) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const sev = SEVERITY_CONFIG[event.severity] || SEVERITY_CONFIG.info;
  const stat = STATUS_CONFIG[event.status] || STATUS_CONFIG.open;
  const typeColor = TYPE_COLORS[event.data_type] || { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200' };
  const SevIcon = sev.icon;

  return (
    <div
      className={`border rounded-lg overflow-hidden transition-all ${event.status === 'dismissed' ? 'opacity-50' : ''} ${sev.border}`}
      data-testid={`anomaly-row-${event.id}`}
    >
      <button
        className={`w-full flex items-center gap-3 p-3 text-left hover:bg-slate-50/50 transition-colors ${expanded ? 'bg-slate-50/30' : ''}`}
        onClick={() => setExpanded(!expanded)}
      >
        <SevIcon className={`h-4 w-4 flex-shrink-0 ${sev.color}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className={`text-[10px] ${typeColor.bg} ${typeColor.text} ${typeColor.border}`}>
              {event.data_type_name}
            </Badge>
            <span className="text-sm font-medium text-slate-800 truncate">
              {event.group_label}: {event.group}
            </span>
            <span className="text-xs text-slate-400">{event.period}</span>
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            Value <strong className={sev.color}>{event.value?.toFixed?.(1) ?? event.value}</strong> is{' '}
            <strong>{Math.abs(event.sigma_deviation)}σ {event.direction}</strong> mean ({event.mean})
          </p>
        </div>
        <Badge variant="outline" className={`text-[10px] flex-shrink-0 ${stat.bg} ${stat.color}`}>
          {stat.label}
        </Badge>
        <span className="text-[10px] text-slate-400 flex-shrink-0 w-16 text-right">
          {new Date(event.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </span>
        {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>

      {expanded && (
        <div className="border-t border-slate-100 p-4 bg-white space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div className="bg-slate-50 rounded p-2">
              <span className="text-slate-500">Value</span>
              <p className="font-bold text-slate-900">{event.value?.toFixed?.(2) ?? event.value}</p>
            </div>
            <div className="bg-slate-50 rounded p-2">
              <span className="text-slate-500">Mean</span>
              <p className="font-bold text-slate-900">{event.mean}</p>
            </div>
            <div className="bg-slate-50 rounded p-2">
              <span className="text-slate-500">Std Dev</span>
              <p className="font-bold text-slate-900">{event.stddev}</p>
            </div>
            <div className="bg-slate-50 rounded p-2">
              <span className="text-slate-500">Deviation</span>
              <p className={`font-bold ${sev.color}`}>{event.sigma_deviation}σ {event.direction}</p>
            </div>
          </div>

          {event.acknowledged_by && (
            <p className="text-[11px] text-slate-400">
              Reviewed by {event.acknowledged_by} on {new Date(event.acknowledged_at).toLocaleDateString()}
            </p>
          )}
          {event.dismissed_by && (
            <p className="text-[11px] text-slate-400">
              Dismissed by {event.dismissed_by} on {new Date(event.dismissed_at).toLocaleDateString()}
            </p>
          )}

          <div className="flex items-center gap-2 pt-1">
            {event.status === 'open' && (
              <>
                <Button size="sm" variant="outline" className="text-xs h-7 gap-1 text-blue-600 border-blue-200"
                  onClick={() => onAcknowledge(event.id)} disabled={isActioning} data-testid={`ack-${event.id}`}>
                  <Eye className="h-3 w-3" /> Acknowledge
                </Button>
                <Button size="sm" variant="outline" className="text-xs h-7 gap-1 text-slate-500 border-slate-200"
                  onClick={() => onDismiss(event.id)} disabled={isActioning} data-testid={`dismiss-${event.id}`}>
                  <EyeOff className="h-3 w-3" /> Dismiss
                </Button>
              </>
            )}
            <Button size="sm" variant="ghost" className="text-xs h-7 gap-1 text-teal-600 ml-auto"
              onClick={() => navigate(ROUTE_MAP[event.data_type] || '/score-trends')}
              data-testid={`drilldown-${event.id}`}>
              View Dashboard <ArrowRight className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

const CHAIN_TIER_COLORS = {
  LEI: { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-300', dot: 'bg-teal-500' },
  PE: { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-300', dot: 'bg-indigo-500' },
  ROE: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300', dot: 'bg-amber-500' },
  KPI: { bg: 'bg-rose-100', text: 'text-rose-800', border: 'border-rose-300', dot: 'bg-rose-500' },
};

function CascadeCard({ cascade }) {
  const [expanded, setExpanded] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const sevColor = cascade.severity === 'critical' ? 'border-rose-300 bg-rose-50/30' : 'border-amber-300 bg-amber-50/30';

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      const res = await api.post('/data-management/anomalies/cascades/analyze', {
        events: cascade.events,
        flow: cascade.flow,
        depth: cascade.depth,
      });
      setAnalysis(res.data.analysis);
    } catch (e) {
      toast.error('AI analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className={`border rounded-lg overflow-hidden ${sevColor}`} data-testid={`cascade-${cascade.id}`}>
      <button
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-white/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Flow visualization */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {cascade.flow.map((tier, i) => {
            const tc = CHAIN_TIER_COLORS[tier] || {};
            return (
              <React.Fragment key={`${cascade.id}-tier-${tier}-${i}`}>
                {i > 0 && <ArrowRight className="h-3 w-3 text-slate-400" />}
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${tc.bg} ${tc.text}`}>
                  {tier}
                </span>
              </React.Fragment>
            );
          })}
        </div>

        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-slate-800">
            {cascade.depth}-tier cascade
          </span>
          <span className="text-xs text-slate-500 ml-2">
            {cascade.events.length} linked anomalies
          </span>
        </div>

        <Badge variant="outline" className={`text-[10px] flex-shrink-0 ${
          cascade.severity === 'critical' ? 'text-rose-600 bg-rose-50 border-rose-200' : 'text-amber-600 bg-amber-50 border-amber-200'
        }`}>
          {cascade.severity}
        </Badge>
        {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>

      {expanded && (
        <div className="border-t border-slate-200/60 p-4 bg-white space-y-3">
          {/* Chain flow diagram */}
          <div className="flex items-stretch gap-0 overflow-x-auto pb-2">
            {cascade.events.map((event, i) => {
              const tier = Object.entries(CHAIN_TIER_COLORS).find(([, v]) =>
                event.data_type?.startsWith(Object.keys(TYPE_COLORS).find(k => TYPE_COLORS[k] && event.data_type === k) || '')
              );
              const tierLabel = cascade.flow[i] || '?';
              const tc = CHAIN_TIER_COLORS[tierLabel] || {};
              return (
                <React.Fragment key={event.id}>
                  {i > 0 && (
                    <div className="flex items-center px-1">
                      <div className="w-8 h-0.5 bg-slate-300 relative">
                        <ArrowRight className="h-3 w-3 text-slate-400 absolute -right-1 -top-[5px]" />
                      </div>
                    </div>
                  )}
                  <div className={`border rounded-lg p-2.5 min-w-[160px] ${tc.border || 'border-slate-200'}`}>
                    <div className="flex items-center gap-1.5 mb-1">
                      <div className={`w-2 h-2 rounded-full ${tc.dot || 'bg-slate-400'}`} />
                      <span className="text-[11px] font-bold text-slate-700">{tierLabel}</span>
                      <span className="text-[10px] text-slate-400">{event.period}</span>
                    </div>
                    <p className="text-xs font-medium text-slate-800 truncate">{event.group_label}: {event.group}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[11px] text-slate-600">
                        Value: <strong className={event.direction === 'above' ? 'text-rose-600' : 'text-amber-600'}>
                          {event.value?.toFixed?.(1) ?? event.value}
                        </strong>
                      </span>
                      <span className="text-[10px] text-slate-400">
                        ({Math.abs(event.sigma_deviation)}σ {event.direction})
                      </span>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
          <p className="text-[11px] text-slate-500 italic">
            Upstream anomaly in {cascade.flow[0]} may be propagating downstream to {cascade.flow[cascade.flow.length - 1]}.
            Investigate the root cause in the {cascade.flow[0]} dashboard.
          </p>

          {/* AI Analysis */}
          {!analysis && !analyzing && (
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-8 gap-1.5 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              onClick={handleAnalyze}
              data-testid={`analyze-${cascade.id}`}
            >
              <TrendingUp className="h-3.5 w-3.5" />
              Generate AI Root Cause Analysis
            </Button>
          )}

          {analyzing && (
            <div className="flex items-center gap-2 text-sm text-indigo-600 py-2" data-testid={`analyzing-${cascade.id}`}>
              <Loader2 className="h-4 w-4 animate-spin" />
              Analyzing cascade pattern with AI...
            </div>
          )}

          {analysis && (
            <div className="border border-indigo-200 rounded-lg bg-gradient-to-br from-indigo-50/50 to-white overflow-hidden" data-testid={`analysis-${cascade.id}`}>
              <div className="px-4 py-2.5 bg-indigo-100/50 border-b border-indigo-200 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-indigo-600" />
                <span className="text-xs font-semibold text-indigo-800">AI Root Cause Analysis</span>
                {analysis.urgency && (
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                    analysis.urgency === 'critical' ? 'bg-rose-100 text-rose-700' :
                    analysis.urgency === 'high' ? 'bg-orange-100 text-orange-700' :
                    analysis.urgency === 'medium' ? 'bg-amber-100 text-amber-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {analysis.urgency} urgency
                  </span>
                )}
              </div>
              <div className="p-4 space-y-3">
                {analysis.root_cause_hypothesis && (
                  <div>
                    <p className="text-[11px] font-semibold text-slate-700 mb-1">Root Cause Hypothesis</p>
                    <p className="text-xs text-slate-600 leading-relaxed">{analysis.root_cause_hypothesis}</p>
                  </div>
                )}
                {analysis.contributing_factors?.length > 0 && (
                  <div>
                    <p className="text-[11px] font-semibold text-slate-700 mb-1">Contributing Factors</p>
                    <ul className="space-y-0.5">
                      {analysis.contributing_factors.map((f, i) => (
                        <li key={`factor-${i}`} className="text-xs text-slate-600 flex items-start gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-1.5 flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {analysis.investigation_steps?.length > 0 && (
                  <div>
                    <p className="text-[11px] font-semibold text-slate-700 mb-1">Investigation Steps</p>
                    <ol className="space-y-0.5">
                      {analysis.investigation_steps.map((s, i) => (
                        <li key={`step-${i}`} className="text-xs text-slate-600 flex items-start gap-1.5">
                          <span className="text-indigo-500 font-bold flex-shrink-0">{i + 1}.</span>
                          {s}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
                {analysis.recommended_actions?.length > 0 && (
                  <div>
                    <p className="text-[11px] font-semibold text-slate-700 mb-1">Recommended Actions</p>
                    <ul className="space-y-0.5">
                      {analysis.recommended_actions.map((a, i) => (
                        <li key={`action-${i}`} className="text-xs text-slate-600 flex items-start gap-1.5">
                          <CheckCircle className="h-3 w-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                          {a}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {analysis.urgency_rationale && (
                  <p className="text-[10px] text-slate-400 border-t border-slate-100 pt-2">
                    {analysis.urgency_rationale}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AnomalyDashboardPage() {
  const { isSuperAdmin, viewAsOrg } = useAuth();
  const [data, setData] = useState({ events: [], total: 0, summary: {}, by_type: {} });
  const [trends, setTrends] = useState(null);
  const [trendView, setTrendView] = useState('type');
  const [cascades, setCascades] = useState(null); // 'type' or 'severity'
  const [loading, setLoading] = useState(true);
  const [isActioning, setIsActioning] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');

  // While previewing as a specific org, force-scope the dashboard.
  useEffect(() => {
    if (viewAsOrg?.id) setSelectedOrg(viewAsOrg.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewAsOrg?.id]);

  const fetchAnomalies = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedOrg) params.append('organization_id', selectedOrg);
      if (filterType !== 'all') params.append('data_type', filterType);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterSeverity !== 'all') params.append('severity', filterSeverity);
      params.append('limit', '200');
      const res = await api.get(`/data-management/anomalies?${params}`);
      setData(res.data);
    } catch (e) {
      logger.error('Failed to fetch anomalies:', e);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, filterType, filterStatus, filterSeverity]);

  useEffect(() => {
    fetchAnomalies();
  }, [fetchAnomalies]);

  // Fetch trends + cascades
  useEffect(() => {
    const params = selectedOrg ? `?organization_id=${selectedOrg}` : '';
    api.get(`/data-management/anomalies/trends${params}`).then(r => setTrends(r.data)).catch(() => {});
    api.get(`/data-management/anomalies/cascades${params}`).then(r => setCascades(r.data)).catch(() => {});
  }, [selectedOrg]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    api.get('/rbac/organizations').then(r => setOrganizations(r.data.organizations || [])).catch(() => {});
  }, [isSuperAdmin]);

  const handleAcknowledge = async (id) => {
    setIsActioning(true);
    try {
      await api.post(`/data-management/anomalies/${id}/acknowledge`);
      toast.success('Anomaly acknowledged');
      fetchAnomalies();
    } catch { toast.error('Failed to acknowledge'); }
    finally { setIsActioning(false); }
  };

  const handleDismiss = async (id) => {
    setIsActioning(true);
    try {
      await api.post(`/data-management/anomalies/${id}/dismiss`);
      toast.success('Anomaly dismissed');
      fetchAnomalies();
    } catch { toast.error('Failed to dismiss'); }
    finally { setIsActioning(false); }
  };

  const handleBulkAction = async (action) => {
    const openIds = data.events.filter(e => e.status === 'open').map(e => e.id);
    if (!openIds.length) return;
    setIsActioning(true);
    try {
      await api.post('/data-management/anomalies/bulk-action', { ids: openIds, action });
      toast.success(`${openIds.length} anomalies ${action}d`);
      fetchAnomalies();
    } catch { toast.error(`Bulk ${action} failed`); }
    finally { setIsActioning(false); }
  };

  const { summary, by_type } = data;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  const __pageTheme = getLightSectionTheme('Insights');
  const __heroActions = (
    <>
      {isSuperAdmin && organizations.length > 0 && !viewAsOrg?.id && (
        <select
          value={selectedOrg}
          onChange={(e) => setSelectedOrg(e.target.value)}
          className="text-sm rounded-lg px-3 py-1.5 bg-white border border-slate-200 text-slate-900 focus:ring-2 focus:ring-rose-400 min-w-[180px] [&>option]:text-slate-900 [&>option]:bg-white"
          data-testid="anomaly-org-filter"
        >
          <option value="">My Organization</option>
          {organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      )}
      {viewAsOrg?.id && (
        <span
          className="text-sm rounded-lg px-3 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 inline-flex items-center gap-1.5 min-w-[180px] justify-center"
          data-testid="anomaly-org-filter-locked"
          title="Org filter is locked while previewing — exit preview to widen the view."
        >
          <span className="h-1.5 w-1.5 rounded-full bg-amber-300 animate-pulse" />
          Scoped to {viewAsOrg.name || 'Preview Org'}
        </span>
      )}
      <Button variant="ghost" size="sm" className="text-slate-700 hover:text-slate-700 hover:bg-slate-50" onClick={() => { setLoading(true); fetchAnomalies(); }}>
        <RefreshCw className="h-4 w-4" />
      </Button>
      {isSuperAdmin && selectedOrg === 'org_acme_demo' && (
        <Button
          size="sm"
          onClick={async () => {
            try {
              const res = await api.post('/data-management/anomalies/demo/trigger');
              toast.success(res.data.headline, { description: 'Live demo anomaly injected. Refreshing…' });
              setLoading(true);
              await fetchAnomalies();
            } catch (err) {
              toast.error(err?.response?.data?.detail || 'Failed to trigger demo anomaly');
            }
          }}
          className="bg-indigo-500 hover:bg-indigo-600 text-white gap-1.5 h-8 text-xs"
          data-testid="trigger-demo-anomaly-btn"
          title="Inject a fresh sigma-breach anomaly into the Acme demo tenant (rotates through 6 scenarios)"
        >
          <FlaskConical className="h-3.5 w-3.5" />
          Trigger demo anomaly
        </Button>
      )}
    </>
  );
  const __heroStats = [
    {
      label: 'Total Anomalies',
      value: summary.total || 0,
      testId: 'anomaly-stat-total',
      whyPanel: {
        label: 'Total Anomalies',
        value: summary.total || 0,
        description: 'Every imported value the engine flagged as a statistical outlier (≥ 2σ from the rolling mean of its measure) since the dataset began.',
        formula: 'count(rows WHERE |z_score| ≥ 2.0)',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'A growing total is fine if your data volume is also growing — what matters is the OPEN tile. Total is a lifetime counter.' },
          { label: 'What to do', value: '', hint: 'Use this as a denominator. The ratio of Reviewed÷Total tells you whether your team is keeping up with the alert stream.' },
        ],
      },
    },
    {
      label: 'Open',
      value: summary.open || 0,
      testId: 'anomaly-stat-open',
      whyPanel: {
        label: 'Open Anomalies',
        value: summary.open || 0,
        description: 'Anomalies that have not yet been acknowledged or dismissed. These are your active triage queue.',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'Open should trend toward zero. > 10 open with new ones arriving each week means you have a triage backlog — assign an owner.' },
          { label: 'What to do', value: '', hint: (summary.open || 0) === 0
              ? 'Inbox zero — keep it that way. Set a weekly cadence to keep this number near zero.'
              : (summary.open || 0) <= 5
                ? 'Manageable. Open the table and review each — most are quickly Acknowledged or Dismissed.'
                : 'Backlog forming. Triage the oldest first; cluster repeats in the same measure into one Acknowledge action.' },
        ],
      },
    },
    {
      label: 'Reviewed',
      value: summary.acknowledged || 0,
      testId: 'anomaly-stat-reviewed',
      whyPanel: {
        label: 'Reviewed Anomalies',
        value: summary.acknowledged || 0,
        description: 'Anomalies you Acknowledged — the data point is genuinely unusual, but you have looked at it and confirmed it represents real movement (e.g. a one-off spike from a launch).',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'High reviewed-rate = healthy data hygiene. The narrative for each acknowledgement should be captured in the comments so you can refer back during quarterly reviews.' },
          { label: 'What to do', value: '', hint: 'Periodically scan the Reviewed list for patterns. If 5 anomalies on the same measure all turned out to be real, the underlying KPI may need its threshold widened.' },
        ],
      },
    },
    {
      label: 'Dismissed',
      value: summary.dismissed || 0,
      testId: 'anomaly-stat-dismissed',
      whyPanel: {
        label: 'Dismissed Anomalies',
        value: summary.dismissed || 0,
        description: 'Anomalies you Dismissed — the spike was an artifact (test data, bad import, sensor error) and is excluded from downstream calculations.',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'A high dismissal rate often signals a data-quality issue upstream. If > 30% of anomalies get dismissed, fix the import pipeline rather than triaging downstream.' },
          { label: 'What to do', value: '', hint: 'Open a dismissed anomaly to see the comment. If you spot a pattern (always the same source file, same time of day), open Data Management to fix the root cause.' },
        ],
        links: [
          { label: 'Open Data Management', path: '/data-management' },
        ],
      },
    },
  ];
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Anomaly Dashboard"
        description="Monitor statistical anomalies detected across the full value chain. Review, acknowledge, or dismiss outliers from imported data."
        theme={__pageTheme}
        icon={AlertTriangle}
        actions={__heroActions}
        stats={__heroStats}
        testId="anomaly-dashboard-hero"
      />
    )}>
    <div className="space-y-6" data-testid="anomaly-dashboard-page">

        {/* Anomaly Trend Chart */}
        {trends && trends.months.length > 0 && (
          <Card data-testid="anomaly-trend-chart">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-slate-600" />
                    Anomaly Frequency Over Time
                    {trends.trend === 'increasing' && (
                      <span className="flex items-center gap-1 text-xs font-normal text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full">
                        <TrendingUp className="h-3 w-3" /> Increasing
                      </span>
                    )}
                    {trends.trend === 'decreasing' && (
                      <span className="flex items-center gap-1 text-xs font-normal text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                        <TrendingDown className="h-3 w-3" /> Improving
                      </span>
                    )}
                  </CardTitle>
                  <CardDescription className="text-xs mt-1">
                    Monthly anomaly counts across the value chain
                  </CardDescription>
                </div>
                <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-100" data-testid="trend-view-toggle">
                  <button
                    onClick={() => setTrendView('type')}
                    className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                      trendView === 'type' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    By Data Type
                  </button>
                  <button
                    onClick={() => setTrendView('severity')}
                    className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                      trendView === 'severity' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    By Severity
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-64" data-testid="trend-chart-container">
                <ResponsiveContainer width="100%" height="100%">
                  {trendView === 'type' ? (
                    <ComposedChart
                      data={trends.months.map(m => {
                        const row = { month: m, ...(trends.by_type[m] || {}), total: trends.totals[m] || 0 };
                        return row;
                      })}
                      margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }}
                        formatter={(value, name) => [value, name.replace(/_/g, ' ')]}
                      />
                      <Legend wrapperStyle={{ paddingTop: '8px' }} formatter={v => <span className="text-xs text-slate-600">{v.replace(/_/g, ' ')}</span>} />
                      <Bar dataKey="kpi_actuals" stackId="a" fill="#f43f5e" name="KPI Actuals" radius={[0, 0, 0, 0]} />
                      <Bar dataKey="lei_averaged" stackId="a" fill="#14b8a6" name="LEI Averaged" />
                      <Bar dataKey="lei_raw" stackId="a" fill="#5eead4" name="LEI Raw" />
                      <Bar dataKey="pe_capability" stackId="a" fill="#6366f1" name="PE Capability" />
                      <Bar dataKey="pe_retention" stackId="a" fill="#a5b4fc" name="PE Retention" />
                      <Bar dataKey="roe_motivation" stackId="a" fill="#f59e0b" name="ROE Motivation" />
                      <Bar dataKey="roe_l2" stackId="a" fill="#fbbf24" name="ROE L2" />
                      <Bar dataKey="roe_l3" stackId="a" fill="#fcd34d" name="ROE L3" radius={[4, 4, 0, 0]} />
                      <Line type="monotone" dataKey="total" stroke="#1e293b" strokeWidth={2} dot={{ r: 4, fill: '#1e293b' }} name="Total" />
                    </ComposedChart>
                  ) : (
                    <ComposedChart
                      data={trends.months.map(m => {
                        const row = { month: m, ...(trends.by_severity[m] || {}), total: trends.totals[m] || 0 };
                        return row;
                      })}
                      margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
                      <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }} />
                      <Legend wrapperStyle={{ paddingTop: '8px' }} formatter={v => <span className="text-xs text-slate-600 capitalize">{v}</span>} />
                      <Bar dataKey="critical" stackId="a" fill="#ef4444" name="Critical" radius={[0, 0, 0, 0]} />
                      <Bar dataKey="warning" stackId="a" fill="#f59e0b" name="Warning" />
                      <Bar dataKey="info" stackId="a" fill="#3b82f6" name="Info" radius={[4, 4, 0, 0]} />
                      <Line type="monotone" dataKey="total" stroke="#1e293b" strokeWidth={2} dot={{ r: 4, fill: '#1e293b' }} name="Total" />
                    </ComposedChart>
                  )}
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Cascade Alerts */}
        {cascades && cascades.cascades.length > 0 && (
          <Card className="border-orange-200 bg-gradient-to-br from-orange-50/50 to-white" data-testid="cascade-alerts-section">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
                Cascade Alerts
                <span className="text-xs font-normal text-slate-500 ml-1">
                  {cascades.total} chain{cascades.total !== 1 ? 's' : ''} detected
                </span>
                {cascades.summary.critical > 0 && (
                  <Badge variant="outline" className="text-rose-600 border-rose-200 bg-rose-50 text-[10px]">
                    {cascades.summary.critical} critical
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="text-xs">
                Linked anomalies flowing through the value chain (LEI → PE → ROE → KPI). Upstream issues may be causing downstream degradation.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {cascades.cascades.map((cascade) => (
                <CascadeCard key={cascade.id} cascade={cascade} />
              ))}
            </CardContent>
          </Card>
        )}

        {/* Type breakdown */}
        {Object.keys(by_type).length > 0 && (
          <div className="flex flex-wrap gap-2">
            {Object.entries(by_type).map(([dtype, stats]) => {
              const tc = TYPE_COLORS[dtype] || {};
              return (
                <button
                  key={dtype}
                  onClick={() => setFilterType(filterType === dtype ? 'all' : dtype)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs transition-colors ${
                    filterType === dtype ? `${tc.bg} ${tc.text} ${tc.border} ring-2 ring-offset-1` : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                  data-testid={`filter-type-${dtype}`}
                >
                  <span className="font-medium">{dtype.replace(/_/g, ' ')}</span>
                  <span className="bg-white border border-slate-200 px-1.5 rounded-full text-[10px] font-bold">{stats.total}</span>
                  {stats.open > 0 && <span className="text-orange-600 text-[10px]">{stats.open} open</span>}
                </button>
              );
            })}
          </div>
        )}

        {/* Filters + Actions bar */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Filter className="h-4 w-4 text-slate-400" />
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="acknowledged">Reviewed</SelectItem>
                <SelectItem value="dismissed">Dismissed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterSeverity} onValueChange={setFilterSeverity}>
              <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="filter-severity">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severity</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="info">Info</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-xs text-slate-400">{data.events.length} result{data.events.length !== 1 ? 's' : ''}</span>
          </div>
          {(summary.open || 0) > 0 && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="text-xs h-7 gap-1"
                onClick={() => handleBulkAction('acknowledge')} disabled={isActioning} data-testid="bulk-acknowledge">
                <Eye className="h-3 w-3" /> Acknowledge All Open
              </Button>
              <Button size="sm" variant="outline" className="text-xs h-7 gap-1 text-slate-500"
                onClick={() => handleBulkAction('dismiss')} disabled={isActioning} data-testid="bulk-dismiss">
                <EyeOff className="h-3 w-3" /> Dismiss All Open
              </Button>
            </div>
          )}
        </div>

        {/* Timeline */}
        {data.events.length > 0 ? (
          <div className="space-y-2">
            {data.events.map(event => (
              <AnomalyRow
                key={event.id}
                event={event}
                onAcknowledge={handleAcknowledge}
                onDismiss={handleDismiss}
                isActioning={isActioning}
              />
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-16 text-center">
              <CheckCircle className="h-12 w-12 text-emerald-700 mx-auto mb-4" />
              <p className="text-lg font-medium text-slate-700">No anomalies detected</p>
              <p className="text-sm text-slate-400 mt-1">
                {filterType !== 'all' || filterStatus !== 'all' || filterSeverity !== 'all'
                  ? 'Try adjusting your filters to see more results.'
                  : 'Import data via the Data Hub to start anomaly detection across the value chain.'}
              </p>
            </CardContent>
          </Card>
        )}
    </div>
    </PageShell>
  );
}
